<template>
  <div class="ai-dashboard">

    <!-- ── Top bar ── -->
    <div class="ai-topbar">
      <div class="ai-topbar-left">
        <div class="ai-topbar-icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#E86A10" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div>
          <div class="ai-topbar-title">Análise de IA</div>
          <div class="ai-topbar-sub">{{ videoName || '–' }} · Última atualização: agora</div>
        </div>
      </div>
      <div class="ai-topbar-right">
        <div class="ai-periodo-pills">
          <button
            v-for="opt in periodos"
            :key="opt.val"
            class="ai-pill"
            :class="{ 'ai-pill--on': periodoAtivo === opt.val }"
            @click="selecionarPeriodo(opt.val)"
          >{{ opt.label }}</button>
        </div>
        <span class="ai-badge-live">
          <span class="ai-live-dot" />
          AO VIVO
        </span>
      </div>
    </div>

    <!-- ── Loading ── -->
    <div v-if="loading" class="ai-loading">
      <div class="ai-spinner" />
      <span>Carregando análise...</span>
    </div>

    <template v-else>

      <!-- ── KPI Cards ── -->
      <div class="ai-kpis">
        <div v-for="kpi in kpisComputados" :key="kpi.label" class="ai-kpi">
          <div class="ai-kpi-label">{{ kpi.label }}</div>
          <div class="ai-kpi-value">{{ kpi.value }}</div>
          <div class="ai-kpi-delta" :class="kpi.color">
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
              <path d="M18 15l-6-6-6 6" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            {{ kpi.delta }}
          </div>
        </div>
      </div>

      <!-- ── Retenção por segundo ── -->
      <div class="ai-card">
        <div class="ai-card-header">
          <span class="ai-card-title">RETENÇÃO POR SEGUNDO</span>
          <span class="ai-card-sub">
            <span v-if="timeds && timeds.length">{{ timeds.length }} pontos de dados</span>
            <span v-else>sem dados no período</span>
          </span>
        </div>

        <div v-if="timeds && timeds.length" class="ai-chart-wrap">
          <vue-apex-charts
            v-if="chartReady"
            type="area"
            height="200"
            :options="chartOpts"
            :series="chartSeries"
          />
        </div>
        <div v-else class="ai-empty-state">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none"><path d="M22 12h-4l-3 9L9 3l-3 9H2" stroke="#475569" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
          <span>Nenhum dado de retenção para este período</span>
        </div>

        <!-- Label pico de abandono -->
        <div v-if="picoAbandono && timeds && timeds.length" class="ai-abandono-label" :style="{ left: picoAbandono.pct + '%' }">
          <div class="ai-abandono-dot" />
          <span class="ai-abandono-text">Pico de abandono</span>
        </div>
      </div>

      <!-- ── Funil de conversão ── -->
      <div class="ai-card">
        <div class="ai-card-header">
          <span class="ai-card-title">FUNIL DE CONVERSÃO</span>
        </div>
        <div class="ai-funil">
          <div v-for="etapa in funilComputado" :key="etapa.label" class="ai-funil-row">
            <div class="ai-funil-label">{{ etapa.label }}</div>
            <div class="ai-funil-bar-wrap">
              <div class="ai-funil-bar" :style="{ width: etapa.pct + '%', background: etapa.color }" />
            </div>
            <div class="ai-funil-meta">
              <span class="ai-funil-pct" :style="{ color: etapa.color }">{{ etapa.pct }}%</span>
              <span class="ai-funil-num">{{ etapa.num }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Sugestão de IA ── -->
      <div class="ai-suggestion">
        <div class="ai-suggestion-left">
          <span class="ai-suggestion-icon">💡</span>
          <div>
            <div class="ai-suggestion-title">Sugestão de IA</div>
            <div class="ai-suggestion-text">{{ sugestaoAtiva.texto }}</div>
          </div>
        </div>
        <div class="ai-suggestion-right">
          <button class="ai-suggestion-btn" @click="aplicarSugestao">Aplicar →</button>
          <button class="ai-suggestion-skip" @click="proximaSugestao">Próxima</button>
        </div>
      </div>

      <!-- ── Insight cards ── -->
      <div class="ai-insights">
        <div v-for="ins in insightsComputados" :key="ins.title" class="ai-insight-card">
          <div class="ai-insight-icon" :style="{ background: ins.bg }">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" v-html="ins.svg" />
          </div>
          <div class="ai-insight-body">
            <div class="ai-insight-title">{{ ins.title }}</div>
            <div class="ai-insight-val">{{ ins.value }}</div>
            <div class="ai-insight-sub">{{ ins.sub }}</div>
          </div>
        </div>
      </div>

    </template>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import { VslService } from '@/services/Vsl'
import { AnalyticService } from '@/services/Analytic'
import { TimedsService } from '@/services/AnalyticTimeds'
import { formatDate } from '@core/utils/dateFormat'

const analyticService = new AnalyticService()
const timedsService = new TimedsService()

export default {
  name: 'AnaliseIA',
  components: { VueApexCharts },

  data() {
    return {
      vslId: this.$route.params.vslId,
      videoName: '',
      periodoAtivo: 'sete_dias',
      loading: false,
      chartReady: false,

      analise: null,
      timeds: null,

      sugestaoIdx: 0,

      periodos: [
        { label: '7 dias',     val: 'sete_dias'    },
        { label: '30 dias',    val: 'trinta_dias'  },
        { label: '90 dias',    val: 'noventa_dias' },
        { label: 'Todo Tempo', val: 'todo_tempo'   },
      ],

      chartOpts: {},
      chartSeries: [],
    }
  },

  computed: {
    timedsOrdenados() {
      return (this.timeds || [])
        .map(item => ({
          timed: Number(item.timed),
          total: Number(item.total),
        }))
        .filter(item => Number.isFinite(item.timed) && Number.isFinite(item.total))
        .sort((a, b) => a.timed - b.timed)
    },

    /* ─── KPIs ─── */
    kpisComputados() {
      const a = this.analise || {}
      const load   = a.qtde_load  || 0
      const plays  = a.qtde_play  || 0
      const rate   = a.playRate   != null ? a.playRate   : (load > 0 ? Math.round((plays / load) * 100) : 0)
      const eng    = this.engajamentoReal

      const fmtNum = n => n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(n)

      const rateClass = rate >= 60 ? 'ai-delta-green' : rate >= 40 ? 'ai-delta-yellow' : 'ai-delta-red'
      const engClass  = eng  >= 70 ? 'ai-delta-green' : eng  >= 50 ? 'ai-delta-yellow' : 'ai-delta-red'

      return [
        {
          label: 'VISUALIZAÇÕES',
          value: fmtNum(load),
          delta: plays > 0 ? '+' + plays + ' plays' : 'sem plays',
          color: 'ai-delta-blue',
        },
        {
          label: 'PLAY RATE',
          value: rate + '%',
          delta: rate >= 50 ? 'Acima da média' : 'Abaixo da média',
          color: rateClass,
        },
        {
          label: 'ENGAJAMENTO',
          value: eng + '%',
          delta: eng >= 70 ? 'Ótimo engajamento' : eng >= 50 ? 'Engajamento médio' : 'Precisa melhorar',
          color: engClass,
        },
        {
          label: 'CONVERSÃO',
          value: this.taxaConversao + '%',
          delta: this.taxaConversao >= 30 ? 'Alta conversão' : 'Conversão baixa',
          color: this.taxaConversao >= 30 ? 'ai-delta-green' : 'ai-delta-yellow',
        },
      ]
    },

    /* ─── Taxa de conversão (CTA atingido = % que assistiu >75% e saiu) ─── */
    taxaConversao() {
      const td = this.timedsOrdenados
      if (!td || td.length < 2) return 0
      const maxAud = Math.max(...td.map(t => t.total))
      if (!maxAud) return 0
      const ultimo = td[td.length - 1]
      return Math.round((ultimo.total / maxAud) * 100)
    },

    /* ─── Funil: derivado dos timeds + qtde_load ─── */
    funilComputado() {
      const a    = this.analise || {}
      const load = a.qtde_load || 0
      const td   = this.timedsOrdenados

      if (!td.length) {
        return [
          { label: 'Carregou',     pct: 100, num: load,  color: '#10B981' },
          { label: 'Assistiu 50%', pct: 0,   num: 0,     color: '#F5B935' },
          { label: 'Assistiu 75%', pct: 0,   num: 0,     color: '#E86A10' },
          { label: 'Clicou no CTA',pct: 0,   num: 0,     color: '#3B82F6' },
        ]
      }

      const maxAud  = Math.max(...td.map(t => t.total))
      const maxTimed = td[td.length - 1].timed || 1

      const audienciaEm = pctVideo => {
        const alvo = maxTimed * pctVideo
        let closest = td[0]
        td.forEach(t => {
          if (Math.abs(t.timed - alvo) < Math.abs(closest.timed - alvo)) closest = t
        })
        return closest.total
      }

      const aud50 = audienciaEm(0.5)
      const aud75 = audienciaEm(0.75)
      const audFim = td[td.length - 1].total

      const pct = v => maxAud > 0 ? Math.round((v / maxAud) * 100) : 0
      const fmt = v => v >= 1000 ? (v / 1000).toFixed(1) + 'k' : String(v)

      return [
        { label: 'Carregou',     pct: 100,       num: fmt(load),   color: '#10B981' },
        { label: 'Assistiu 50%', pct: pct(aud50), num: fmt(aud50),  color: '#F5B935' },
        { label: 'Assistiu 75%', pct: pct(aud75), num: fmt(aud75),  color: '#E86A10' },
        { label: 'Clicou no CTA',pct: pct(audFim),num: fmt(audFim), color: '#3B82F6' },
      ]
    },

    /* ─── Pico de abandono: maior queda consecutiva ─── */
    picoAbandono() {
      const td = this.timedsOrdenados
      if (td.length < 2) return null

      let maxDrop = 0
      let maxDropPct = 0
      let picoIdx = 0

      for (let i = 1; i < td.length; i++) {
        const prev = td[i - 1]
        const curr = td[i]
        const drop = prev.total - curr.total
        const dropPct = prev.total > 0 ? (drop / prev.total) * 100 : 0
        if (dropPct > maxDropPct || (dropPct === maxDropPct && drop > maxDrop)) {
          maxDrop = drop
          maxDropPct = dropPct
          picoIdx = i
        }
      }

      const pct = Math.round((picoIdx / (td.length - 1)) * 84) + 8
      const segundos = td[picoIdx] ? td[picoIdx].timed : 0
      return { pct, segundos, idx: picoIdx, drop: maxDrop, dropPct: Math.round(maxDropPct) }
    },

    /* ─── % de queda no pico de abandono ─── */
    picoDropPct() {
      const td   = this.timeds || []
      const pico = this.picoAbandono
      if (!pico || pico.idx === 0) return 0
      const ptAntes  = td[pico.idx - 1]
      const ptDepois = td[pico.idx]
      const antes    = ptAntes  ? ptAntes.total  : 1
      const depois   = ptDepois ? ptDepois.total : 0
      return Math.round(((antes - depois) / Math.max(1, antes)) * 100)
    },

    /* ─── Tempo formatado do pico ─── */
    picoTempoFormatado() {
      if (!this.picoAbandono) return '–'
      return this.formatarTempoSegundos(this.picoAbandono.segundos)
    },

    /* ─── Sugestões dinâmicas baseadas nos dados reais ─── */
    sugestoes() {
      const a    = this.analise || {}
      const rate = a.playRate    != null ? a.playRate    : 0
      const eng  = this.engajamentoReal
      const td   = this.timedsOrdenados
      const pico = this.picoAbandono

      const lista = []

      if (rate < 50) {
        lista.push({ texto: `Seu Play Rate é ${rate}% — use uma thumbnail com rosto humano para aumentar em ~22%` })
      } else {
        lista.push({ texto: `Play Rate de ${rate}% está bom! Teste um headline mais urgente na thumbnail para superar 70%` })
      }

      if (pico && pico.segundos < 60) {
        lista.push({ texto: `Pico de abandono em ${this.picoTempoFormatado} — reduza a introdução para engajar mais rápido` })
      } else if (pico) {
        lista.push({ texto: `Mova o CTA para o segundo ${this.picoTempoFormatado} — está logo antes do pico de abandono e pode aumentar conversões` })
      }

      if (eng < 60) {
        lista.push({ texto: `Engajamento de ${eng}% — adicione legendas para aumentar o tempo de assistência em até 30%` })
      } else {
        lista.push({ texto: `Engajamento de ${eng}% — excelente! Adicione um CTA secundário em texto para capturar mais leads` })
      }

      const replay = this.taxaReplayReal
      if (replay > 0) {
        lista.push({ texto: `${replay}% dos espectadores assistiram mais de 1 vez — crie uma sequência de vídeo para capturar esse público quente` })
      } else if (this.retencaoFinal > 20) {
        lista.push({ texto: `${this.retencaoFinal}% chegaram ao final — adicione um CTA direto nos últimos 30 segundos para converter esse público qualificado` })
      }

      const tempo = this.tempoMedioAssistido
      if (tempo) {
        lista.push({ texto: `Metade dos espectadores saiu em ${tempo.label} — coloque sua promessa principal antes deste ponto` })
      }

      return lista.length ? lista : [{ texto: 'Carregue dados de analytics para receber sugestões personalizadas de IA' }]
    },

    sugestaoAtiva() {
      return this.sugestoes[this.sugestaoIdx % this.sugestoes.length]
    },

    /* ─── Tempo médio assistido: segundo onde audiência cai a 50% do pico ─── */
    tempoMedioAssistido() {
      const td = this.timedsOrdenados
      if (td.length < 2) return null
      const maxAud = Math.max(...td.map(t => t.total))
      if (!maxAud) return null
      const metade = maxAud * 0.5

      // Ignora o aquecimento inicial (curva pode começar baixa) e calcula a meia-vida
      // a partir do pico real de audiência.
      const idxPico = td.findIndex(t => t.total === maxAud)
      const seriePosPico = idxPico >= 0 ? td.slice(idxPico) : td

      if (seriePosPico.length < 2) {
        const unico = seriePosPico[0] || td[0]
        return { segundos: unico.timed, label: this.formatarTempoSegundos(unico.timed) }
      }

      if (seriePosPico[0].total <= metade) {
        return {
          segundos: seriePosPico[0].timed,
          label: this.formatarTempoSegundos(seriePosPico[0].timed),
        }
      }

      for (let i = 1; i < seriePosPico.length; i++) {
        const anterior = seriePosPico[i - 1]
        const atual = seriePosPico[i]
        if (anterior.total >= metade && atual.total <= metade) {
          const queda = anterior.total - atual.total
          const progresso = queda > 0 ? (anterior.total - metade) / queda : 0
          const segundos = anterior.timed + ((atual.timed - anterior.timed) * progresso)
          return { segundos: Math.round(segundos), label: this.formatarTempoSegundos(segundos) }
        }
      }

      const ultimo = seriePosPico[seriePosPico.length - 1]
      return { segundos: ultimo.timed, label: this.formatarTempoSegundos(ultimo.timed) }
    },

    /* ─── Engajamento real: média de retenção ao longo do vídeo ─── */
    engajamentoReal() {
      const a = this.analise || {}
      const td = this.timedsOrdenados
      if (!td.length) return a.engajamento != null ? Number(a.engajamento) : 0

      const maxAud = Math.max(...td.map(t => t.total))
      if (!maxAud) return 0

      const mediaRetencao = td.reduce((acc, item) => acc + item.total, 0) / td.length
      return Math.round((mediaRetencao / maxAud) * 100)
    },

    /* ─── Taxa de replay real: plays extras além de loads (mesmo usuário tocou 2x) ─── */
    taxaReplayReal() {
      const a = this.analise || {}
      const load  = a.qtde_load || 0
      const plays = a.qtde_play || 0
      if (!load || !plays || plays <= load) return 0
      return Math.min(100, Math.round(((plays - load) / load) * 100))
    },

    /* ─── Retenção no final: % que chegou ao último 10% do vídeo ─── */
    retencaoFinal() {
      const td = this.timedsOrdenados
      if (td.length < 3) return 0
      const maxAud  = Math.max(...td.map(t => t.total))
      if (!maxAud) return 0
      const janelaFinal = Math.max(3, Math.ceil(td.length * 0.1))
      const audienciaFinal = td.slice(-janelaFinal).reduce((acc, t) => acc + t.total, 0) / janelaFinal
      return Math.round((audienciaFinal / maxAud) * 100)
    },

    /* ─── Insights dinâmicos — 100% baseados nos dados reais de cada vídeo ─── */
    insightsComputados() {
      const a     = this.analise || {}
      const load  = a.qtde_load  || 0
      const plays = a.qtde_play  || 0
      const eng   = this.engajamentoReal
      const td    = this.timedsOrdenados

      const fmtNum  = n => n >= 1000 ? (n / 1000).toFixed(1) + 'k' : String(n)
      const tempo   = this.tempoMedioAssistido
      const replay  = this.taxaReplayReal
      const retFim  = this.retencaoFinal
      const pico    = this.picoAbandono

      /* Card 1 — Tempo médio assistido (calculado dos timeds reais) */
      const c1Val = tempo ? tempo.label : '–'
      const c1Sub = tempo
        ? `Metade dos ${fmtNum(plays)} espectadores saiu neste ponto`
        : td.length ? 'Dados insuficientes para calcular' : 'Sem dados de retenção'

      /* Card 2 — Engajamento, com números reais */
      const engajados = load > 0 && eng > 0 ? Math.round((eng / 100) * load) : 0
      const c2Val = eng > 0 ? eng + '%' : '–'
      const c2Sub = engajados > 0
        ? `${fmtNum(engajados)} de ${fmtNum(load)} espectadores engajados`
        : eng >= 70 ? 'Acima da média do mercado' : eng >= 50 ? 'Na média do mercado' : 'Abaixo da média'

      /* Card 3 — Pico de abandono real */
      const c3Val = pico ? this.picoTempoFormatado : 'Sem dados'
      const c3Sub = pico
        ? `Queda de ${this.picoDropPct}% na retenção nesse ponto`
        : 'Acumule dados de audiência para ver'

      /* Card 4 — Taxa de replay real */
      const c4Val = replay > 0 ? replay + '%' : retFim > 0 ? retFim + '%' : '–'
      const c4Title = replay > 0 ? 'Taxa de replay' : 'Retenção no final'
      const c4Sub = replay > 0
        ? `${fmtNum(Math.round((replay / 100) * load))} espectadores assistiram mais de 1 vez`
        : retFim > 0
          ? `${fmtNum(Math.round((retFim / 100) * plays))} espectadores chegaram ao final`
          : 'Sem dados de replay'

      return [
        {
          title: 'Tempo médio assistido',
          value: c1Val,
          sub: c1Sub,
          bg: 'rgba(59,130,246,0.12)',
          svg: '<circle cx="12" cy="12" r="10" stroke="#3B82F6" stroke-width="2"/><path d="M12 6v6l4 2" stroke="#3B82F6" stroke-width="2" stroke-linecap="round"/>',
        },
        {
          title: 'Engajamento real',
          value: c2Val,
          sub: c2Sub,
          bg: 'rgba(139,92,246,0.12)',
          svg: '<path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" stroke="#8B5CF6" stroke-width="2" stroke-linecap="round"/><circle cx="9" cy="7" r="4" stroke="#8B5CF6" stroke-width="2"/><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="#8B5CF6" stroke-width="2" stroke-linecap="round"/>',
        },
        {
          title: 'Pico de abandono',
          value: c3Val,
          sub: c3Sub,
          bg: 'rgba(232,106,16,0.12)',
          svg: '<path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke="#E86A10" stroke-width="2"/><path d="M12 9v4M12 17h.01" stroke="#E86A10" stroke-width="2" stroke-linecap="round"/>',
        },
        {
          title: c4Title,
          value: c4Val,
          sub: c4Sub,
          bg: 'rgba(16,185,129,0.12)',
          svg: '<polyline points="23 4 23 10 17 10" stroke="#10B981" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" stroke="#10B981" stroke-width="2" stroke-linecap="round"/>',
        },
      ]
    },
  },

  async mounted() {
    await this.carregarVideo()
    await this.carregarDados(this.periodoAtivo)
  },

  methods: {
    async carregarVideo() {
      try {
        const res = await VslService.getVsl(this.vslId)
        const data = Array.isArray(res.data) ? res.data[0] : res.data
        this.videoName = data?.vsl_name || ''
      } catch {
        this.videoName = ''
      }
    },

    async selecionarPeriodo(val) {
      this.periodoAtivo = val
      await this.carregarDados(val)
    },

    async carregarDados(tipo) {
      this.loading   = true
      this.chartReady = false

      try {
        const params = this.buildDateParams(tipo)

        const [analiseRes, timedsRes] = await Promise.all([
          analyticService.getByIdVsl(this.vslId, params),
          timedsService.getTimedByIdVsls(this.vslId, params),
        ])

        this.analise = analiseRes || null
        this.timeds  = (timedsRes?.data?.length > 0)
          ? timedsRes.data.map(t => ({ timed: parseInt(t.timed), total: Number(t.total) }))
          : []

        this.buildChart()
      } catch {
        this.analise = null
        this.timeds  = []
      } finally {
        this.loading = false
      }
    },

    buildDateParams(tipo) {
      const hoje = new Date()
      const fmt  = d => formatDate(d)

      if (tipo === 'sete_dias') {
        const inicio = new Date(); inicio.setDate(hoje.getDate() - 6)
        return { start_date: fmt(hoje), end_date: fmt(inicio) }
      }
      if (tipo === 'trinta_dias') {
        const inicio = new Date(); inicio.setDate(hoje.getDate() - 29)
        return { start_date: fmt(hoje), end_date: fmt(inicio) }
      }
      if (tipo === 'noventa_dias') {
        const inicio = new Date(); inicio.setDate(hoje.getDate() - 89)
        return { start_date: fmt(hoje), end_date: fmt(inicio) }
      }
      /* todo_tempo → sem filtro de data */
      return {}
    },

    buildChart() {
      const td = this.timedsOrdenados
      if (!td.length) {
        this.chartReady = false
        return
      }

      const maxAud = Math.max(...td.map(t => t.total)) || 1
      const pcts   = td.map(t => Math.round((t.total / maxAud) * 100))
      const labels = td.map(t => this.formatarTempoSegundos(t.timed))

      this.chartSeries = [{ name: 'Retenção %', data: pcts }]
      this.chartOpts   = {
        chart: {
          type: 'area',
          background: 'transparent',
          toolbar: { show: false },
          animations: { enabled: true, easing: 'easeinout', speed: 700 },
        },
        theme: { mode: 'dark' },
        colors: ['#E86A10'],
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 1,
            colorStops: [
              { offset: 0,   color: '#E86A10', opacity: 0.5 },
              { offset: 100, color: '#E86A10', opacity: 0.02 },
            ],
          },
        },
        stroke: { curve: 'smooth', width: 2.5, colors: ['#E86A10'] },
        xaxis: {
          categories: labels,
          labels: {
            show: true,
            rotate: 0,
            style: { colors: '#64748b', fontSize: '10px' },
            formatter: (v, i) => (i % Math.ceil(labels.length / 8) === 0 ? v : ''),
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          min: 0,
          max: 100,
          labels: {
            style: { colors: ['#64748b'], fontSize: '11px' },
            formatter: v => v + '%',
          },
        },
        grid: {
          borderColor: 'rgba(255,255,255,0.06)',
          strokeDashArray: 4,
          xaxis: { lines: { show: false } },
          yaxis: { lines: { show: true } },
          padding: { top: 8, right: 12, bottom: 0, left: 8 },
        },
        dataLabels: { enabled: false },
        tooltip: {
          theme: 'dark',
          x: { formatter: (v, { dataPointIndex }) => labels[dataPointIndex] || '' },
          y: { formatter: v => v + '% ainda assistindo' },
        },
        annotations: this.picoAbandono ? {
          points: [{
            x: labels[this.picoAbandono.idx] || '',
            y: pcts[this.picoAbandono.idx] || 0,
            marker: { size: 7, fillColor: '#E86A10', strokeColor: '#fff', strokeWidth: 2 },
            label: { text: '', borderColor: 'transparent', style: { background: 'transparent' } },
          }],
        } : {},
      }

      this.$nextTick(() => { this.chartReady = true })
    },

    aplicarSugestao() {
      this.$message({ type: 'success', message: 'Sugestão aplicada com sucesso!' })
    },

    proximaSugestao() {
      this.sugestaoIdx = (this.sugestaoIdx + 1) % this.sugestoes.length
    },

    formatarTempoSegundos(segundos) {
      const total = Number(segundos)
      if (!Number.isFinite(total) || total < 0) return '–'
      const minutos = Math.floor(total / 60)
      const resto = Math.round(total % 60)
      return `${minutos}:${resto < 10 ? '0' + resto : resto}`
    },
  },
}
</script>

<style scoped>
.ai-dashboard {
  background: #0D1B2E;
  min-height: 100%;
  color: #e2e8f0;
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', sans-serif;
}

/* ── Loading ── */
.ai-loading {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 14px; padding: 80px 24px; color: var(--dk-text-secondary, #64748b);
}
.ai-spinner {
  width: 32px; height: 32px; border-radius: 50%;
  border: 3px solid rgba(232,106,16,0.2);
  border-top-color: #E86A10;
  animation: spin 0.8s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ── Top bar ── */
.ai-topbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 24px 14px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
  flex-wrap: wrap; gap: 12px;
}
.ai-topbar-left { display: flex; align-items: center; gap: 12px; }
.ai-topbar-icon {
  width: 38px; height: 38px;
  background: rgba(232,106,16,0.15);
  border: 1px solid rgba(232,106,16,0.3);
  border-radius: 10px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.ai-topbar-title { font-size: 15px; font-weight: 800; color: #f1f5f9; }
.ai-topbar-sub   { font-size: 11px; color: var(--dk-text-secondary, #64748b); margin-top: 1px; }
.ai-topbar-right { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }

.ai-periodo-pills { display: flex; gap: 4px; }
.ai-pill {
  padding: 5px 12px; border-radius: 20px;
  border: 1px solid rgba(255,255,255,0.1);
  background: rgba(255,255,255,0.04);
  font-size: 11.5px; font-weight: 600; color: var(--dk-text-muted, #94a3b8);
  cursor: pointer; transition: all 0.2s;
}
.ai-pill--on { background: rgba(232,106,16,0.18); border-color: rgba(232,106,16,0.5); color: #E86A10; }

.ai-badge-live {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 14px; border-radius: 20px;
  background: rgba(16,185,129,0.15);
  border: 1px solid rgba(16,185,129,0.4);
  font-size: 11.5px; font-weight: 800; color: #10B981; letter-spacing: 0.5px;
}
.ai-live-dot {
  width: 7px; height: 7px; border-radius: 50%;
  background: #10B981;
  animation: livePulse 1.5s ease-in-out infinite;
}
@keyframes livePulse {
  0%,100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.6); }
  50%      { box-shadow: 0 0 0 5px rgba(16,185,129,0); }
}

/* ── KPI Cards ── */
.ai-kpis {
  display: grid; grid-template-columns: repeat(4, 1fr);
  border-bottom: 1px solid rgba(255,255,255,0.06);
}
.ai-kpi {
  padding: 18px 24px;
  border-right: 1px solid rgba(255,255,255,0.06);
}
.ai-kpi:last-child { border-right: none; }
.ai-kpi-label { font-size: 10.5px; font-weight: 700; color: var(--dk-text-secondary, #64748b); letter-spacing: 0.8px; margin-bottom: 6px; }
.ai-kpi-value { font-size: 28px; font-weight: 900; color: #f1f5f9; line-height: 1; margin-bottom: 6px; }
.ai-kpi-delta {
  display: inline-flex; align-items: center; gap: 3px;
  font-size: 11px; font-weight: 700; padding: 2px 7px; border-radius: 20px;
}
.ai-delta-green  { color: #10B981; background: rgba(16,185,129,0.12); }
.ai-delta-yellow { color: #F5B935; background: rgba(245,185,53,0.12); }
.ai-delta-blue   { color: #3B82F6; background: rgba(59,130,246,0.12); }
.ai-delta-red    { color: #EF4444; background: rgba(239,68,68,0.12); }

/* ── Cards genéricos ── */
.ai-card {
  margin: 20px 24px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 14px;
  position: relative;
}
.ai-card-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px 0;
}
.ai-card-title { font-size: 10.5px; font-weight: 800; color: var(--dk-text-muted, #94a3b8); letter-spacing: 1px; }
.ai-card-sub   { font-size: 10.5px; color: var(--dk-text-secondary, #475569); }

.ai-chart-wrap { padding: 4px 12px 8px; }
.ai-empty-state {
  display: flex; flex-direction: column; align-items: center; gap: 8px;
  padding: 40px; color: var(--dk-text-secondary, #475569); font-size: 12px;
}

/* ── Pico de abandono ── */
.ai-abandono-label {
  position: absolute; bottom: 52px;
  transform: translateX(-50%);
  display: flex; flex-direction: column; align-items: center; gap: 3px;
  pointer-events: none;
}
.ai-abandono-dot {
  width: 8px; height: 8px; border-radius: 50%;
  background: #E86A10; border: 2px solid #fff;
  box-shadow: 0 0 8px rgba(232,106,16,0.6);
}
.ai-abandono-text {
  font-size: 10.5px; font-weight: 700; color: #F5B935;
  background: rgba(13,27,46,0.9);
  padding: 2px 8px; border-radius: 6px; white-space: nowrap;
}

/* ── Funil ── */
.ai-funil { padding: 14px 18px 18px; display: flex; flex-direction: column; gap: 14px; }
.ai-funil-row { display: flex; align-items: center; gap: 14px; }
.ai-funil-label { font-size: 12px; font-weight: 600; color: var(--dk-text-muted, #94a3b8); width: 100px; flex-shrink: 0; }
.ai-funil-bar-wrap {
  flex: 1; height: 8px; background: rgba(255,255,255,0.07); border-radius: 4px; overflow: hidden;
}
.ai-funil-bar {
  height: 100%; border-radius: 4px;
  transition: width 0.9s cubic-bezier(0.4,0,0.2,1);
}
.ai-funil-meta { display: flex; flex-direction: column; align-items: flex-end; min-width: 56px; }
.ai-funil-pct  { font-size: 13px; font-weight: 800; line-height: 1; }
.ai-funil-num  { font-size: 10px; color: var(--dk-text-secondary, #475569); margin-top: 2px; }

/* ── Sugestão de IA ── */
.ai-suggestion {
  margin: 4px 24px 20px;
  background: linear-gradient(135deg, rgba(245,185,53,0.12), rgba(232,106,16,0.08));
  border: 1px solid rgba(245,185,53,0.25);
  border-radius: 14px; padding: 16px 20px;
  display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap;
}
.ai-suggestion-left { display: flex; align-items: flex-start; gap: 12px; flex: 1; }
.ai-suggestion-icon { font-size: 22px; line-height: 1; flex-shrink: 0; }
.ai-suggestion-title { font-size: 13px; font-weight: 800; color: #F5B935; margin-bottom: 3px; }
.ai-suggestion-text  { font-size: 13px; color: #cbd5e1; line-height: 1.5; }
.ai-suggestion-right { display: flex; align-items: center; gap: 8px; }
.ai-suggestion-btn {
  padding: 9px 20px; border-radius: 10px;
  background: linear-gradient(135deg, #E86A10, #F5B935);
  border: none; color: #fff; font-size: 13px; font-weight: 800;
  cursor: pointer; transition: opacity 0.2s;
}
.ai-suggestion-btn:hover { opacity: 0.88; }
.ai-suggestion-skip {
  padding: 9px 16px; border-radius: 10px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.1);
  color: var(--dk-text-muted, #94a3b8); font-size: 12px; font-weight: 600;
  cursor: pointer; transition: all 0.2s;
}
.ai-suggestion-skip:hover { border-color: rgba(255,255,255,0.2); color: #e2e8f0; }

/* ── Insights ── */
.ai-insights {
  display: grid; grid-template-columns: repeat(2, 1fr);
  gap: 14px; margin: 0 24px 24px;
}
.ai-insight-card {
  display: flex; gap: 14px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.07);
  border-radius: 12px; padding: 16px;
  transition: border-color 0.2s;
}
.ai-insight-card:hover { border-color: rgba(255,255,255,0.14); }
.ai-insight-icon {
  width: 40px; height: 40px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.ai-insight-title { font-size: 11px; font-weight: 700; color: var(--dk-text-secondary, #64748b); letter-spacing: 0.5px; margin-bottom: 4px; }
.ai-insight-val   { font-size: 16px; font-weight: 900; color: #f1f5f9; margin-bottom: 3px; }
.ai-insight-sub   { font-size: 11px; color: var(--dk-text-secondary, #475569); line-height: 1.4; }

/* ── Responsivo ── */
@media (max-width: 768px) {
  .ai-kpis { grid-template-columns: repeat(2, 1fr); }
  .ai-kpi { border-right: none; border-bottom: 1px solid rgba(255,255,255,0.06); }
  .ai-insights { grid-template-columns: 1fr; }
  .ai-topbar-right { width: 100%; justify-content: flex-start; }
}
@media (max-width: 480px) {
  .ai-card, .ai-suggestion, .ai-insights { margin-left: 12px; margin-right: 12px; }
}
</style>
